import { CachePolicies, useFetch } from "use-http";
import { useCallback, useEffect, useState } from "react";
import { Result } from "~/components/job/Job/HostedCompute/types";
import { PercentageComputeChart } from "~/components/job/Job/HostedCompute/PercentageComputeChart";
import { MemoryComputeChart } from "~/components/job/Job/HostedCompute/MemoryComputeChart";
import { LoadingChart } from "~/components/analytics/shared/LoadingStates";

const TERMINAL_JOB_STATES = ["finished", "timed_out", "canceled", "expired"];
const REFRESH_INTERVAL = 5000;

const refresh_jitter = () => Math.floor(Math.random() * 1000);

interface HostedComputeProps {
  job: {
    hostedAgentMetricsPath: string;
    hostedAgentInfoPath: string;
    state: string;
    agentName: string;
    instanceShape?: {
      operatingSystem: string;
    };
  };
}

export const HostedCompute: React.FC<HostedComputeProps> = (props) => {
  return (
    <div data-testid="JobComputeMetricsComponent" className="relative">
      {Features.MdcShowHostedAgentInstanceInfo && <ComputeInfo job={props.job} />}
      <ComputeMetrics job={props.job} />
    </div>
  );
};

interface ComputeMetricsProps {
  job: {
    hostedAgentMetricsPath: string;
    state: string;
    instanceShape?: {
      operatingSystem: string;
    };
  };
  callback?: () => void;
}

export const ComputeMetrics: React.FC<ComputeMetricsProps> = (props) => {
  const metrics = [
    { key: "cpu_breakdown", title: "CPU Breakdown" },
    { key: "mem", title: "Memory Utilization" },
    { key: "storage", title: "Storage Utilization" },
    ...(props.job.instanceShape?.operatingSystem?.toLowerCase() === "linux"
      ? [{ key: "io_wait", title: "IO Wait" }]
      : []),
  ];

  const hostedAgentMetricsDataEndpoint = props.job.hostedAgentMetricsPath + ".json";

  const { get, response, loading, error } = useFetch(hostedAgentMetricsDataEndpoint, {
    retries: 3,
    retryDelay: 5000,
    cacheLife: 4000, // 4 seconds
  });
  const [chartData, setChartData] = useState<Result>();

  const loadInitialChartData = useCallback(async () => {
    const data: Result = await get();
    if (response.ok) {
      setChartData(data);
    }
  }, [get, response]);

  useEffect(() => {
    loadInitialChartData();
  }, [loadInitialChartData]);

  const refreshData = async () => {
    if (loading) {
      return;
    } // don't refresh if we're already loading

    const data: Result = await get();
    if (response.ok) {
      setChartData(data);
    }
  };

  const hasData = !!chartData && Object.keys(chartData.series).length > 0;

  useEffect(() => {
    if (TERMINAL_JOB_STATES.includes(props.job.state) && hasData) {
      return;
    }
    const timerId = setInterval(refreshData, REFRESH_INTERVAL + refresh_jitter());
    return () => clearInterval(timerId);
  });

  if (error) {
    return <div>There was an error loading the chart</div>;
  }

  return (
    <div className="my-3 grid grid-cols-1 gap-4 md:grid-cols-2">
      {metrics.map((metric) => (
        <div
          key={metric.key}
          className="shadow-depth-200 flex-col items-center gap-y-3 rounded-lg bg-white px-3 py-3 font-normal"
        >
          <div>
            <h3 className="mt-0 text-base font-semibold leading-tight">{metric.title}</h3>
          </div>
          <div className="h-72 w-full">
            {chartData?.series?.[metric.key] ? (
              metric.key === "mem" ? (
                <MemoryComputeChart chartData={chartData.series[metric.key]} />
              ) : (
                <PercentageComputeChart chartData={chartData.series[metric.key]} />
              )
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <LoadingChart barWidth="16px" barCount={6} />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

interface ComputeInfoProps {
  job: {
    hostedAgentInfoPath: string;
    agentName: string;
    state: string;
  };
  callback?: () => void;
}

/**
 * Component to display the compute information of the hosted agent
 *
 * @NOTE: This component is only available for customers in the POC stage which need to know the compute information of the hosted agent
 */
export const ComputeInfo: React.FC<ComputeInfoProps> = (props) => {
  const hostedAgentInfoDataEndpoint = props.job.hostedAgentInfoPath + ".json";

  const { get, response, loading, error } = useFetch(hostedAgentInfoDataEndpoint, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const [instanceInfo, setInstanceInfo] = useState<Result>();

  const fetchComputeInfo = useCallback(async () => {
    const data: Result = await get();
    if (response.ok) {
      setInstanceInfo(data);
    }
  }, [get, response]);

  useEffect(() => {
    fetchComputeInfo();
  }, [fetchComputeInfo]);

  if (error) {
    return <div>There was an error loading the chart</div>;
  }

  return (
    <div>
      <div>
        {loading ? (
          <div className="border-gray flex w-full justify-center border p-4">
            <div className="mb-4 h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>
        ) : null}
        {!loading && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="border-gray p-4">
              <span className="semi-bold text-sm">Continent:</span>{" "}
              {instanceInfo?.instance?.geo_continent}
            </div>
            <div className="border-gray p-4">
              <span className="semi-bold text-sm">Platform:</span>{" "}
              {instanceInfo?.instance?.major_hw_platform}
            </div>
            <div className="border-gray p-4">
              <span className="semi-bold text-sm">Architecture:</span>{" "}
              {instanceInfo?.instance?.machine_arch}
            </div>
            <div className="border-gray p-4">
              <span className="semi-bold text-sm">Memory:</span>{" "}
              {instanceInfo?.instance?.memory_megabytes}
            </div>
            <div className="border-gray p-4">
              <span className="semi-bold text-sm">Operating System:</span>{" "}
              {instanceInfo?.instance?.os}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
